import { FC, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import dayjs from 'dayjs';
import { useEndpoints } from '../../../hooks/useEndpoints';
import { IBodyMapTeamAnalytics, BodyMapAnalyticsZone } from '../../../models/bodyMapAlert';
import LeopartLoader from '../../../shared/LeopartLoader/LeopartLoader';
import KpiCard from '../../../shared/KpiCard/KpiCard';
import { ChartErrorState } from '../AthleteInjuryCharts/charts/_shared/ChartErrorState';
import { ChartEmptyState } from '../AthleteInjuryCharts/charts/_shared/ChartEmptyState';
import {
  chartAxisTick,
  chartGridProps,
  chartTooltipContentStyle,
  chartTooltipLabelStyle,
  chartTooltipItemStyle,
} from '../../../shared/charts/chartTheme';
import styles from './TeamBodyMapAnalytics.module.scss';
import { ZONE_LABELS, TYPE_LABELS } from '../../../shared/bodyMap/bodyMapScale';
import CustomTable from '../../../shared/CustomTable/CustomTable';

type RangeDays = 7 | 30 | 90;

const RANGE_OPTIONS: { label: string; days: RangeDays }[] = [
  { label: '7 giorni', days: 7 },
  { label: '30 giorni', days: 30 },
  { label: '90 giorni', days: 90 },
];

/**
 * Colore (classe CSS) dell'intensità media in tabella: qui la semantica è un
 * semaforo di rischio (verde = basso, non "Lieve" come in bodyMapScale — le
 * due scale sono concettualmente diverse, non vanno unificate) sulla media
 * continua, non sulla fascia 1-5 discreta di `getIntensityColor`.
 */
function avgIntensityClass(avg: number): string {
  if (avg >= 4) return styles.avgIntensityHigh;
  if (avg >= 3) return styles.avgIntensityMedium;
  return styles.avgIntensityLow;
}

interface Props {
  teamId: string;
}

const TeamBodyMapAnalytics: FC<Props> = ({ teamId }) => {
  const [rangeDays, setRangeDays] = useState<RangeDays>(30);
  const endpoints = useEndpoints();

  const fromDate = dayjs().subtract(rangeDays - 1, 'day').format('YYYY-MM-DD');
  const toDate = dayjs().format('YYYY-MM-DD');

  const { data, isLoading, isError, refetch } = useQuery<IBodyMapTeamAnalytics>({
    queryKey: ['bodyMapAnalytics', teamId, rangeDays],
    queryFn: async () => {
      const res = await endpoints.getBodyMapTeamAnalytics(teamId, { from: fromDate, to: toDate });
      return res.data;
    },
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className={styles.loadingWrap}>
        <LeopartLoader />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className={styles.errorWrap}>
        <ChartErrorState onRetry={() => refetch()} message="Impossibile caricare i dati analytics." />
      </div>
    );
  }

  const chartData = data.weeklyTrend.map((pt) => ({
    ...pt,
    weekLabel: dayjs(pt.weekStart).format('DD/MM'),
  }));

  return (
    <div className={styles.wrap}>
      {/* Header con range selector */}
      <div className={styles.header}>
        <span className={styles.title}>Analytics Fastidi Fisici</span>
        <div className={styles.rangePicker}>
          {RANGE_OPTIONS.map(({ label, days }) => (
            <button
              key={days}
              onClick={() => setRangeDays(days)}
              className={[
                styles.rangeBtn,
                rangeDays === days ? styles.rangeBtnActive : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div className={styles.kpiGrid}>
        <KpiCard label="Segnalazioni totali" value={String(data.totalComplaints)} />
        <KpiCard label="Zone attive" value={String(data.activeZones)} />
        <KpiCard label="Alert generati" value={String(data.alertsTriggered)} tone="warning" />
        <KpiCard
          label="Complaint → Infortuni"
          value={`${data.complaintToInjuryPct.toFixed(1)}%`}
          tone={data.complaintToInjuryPct > 10 ? 'danger' : 'neutral'}
        />
      </div>

      {/* Trend settimanale */}
      <div className={styles.section}>
        <span className={styles.sectionTitle}>Trend settimanale</span>
        {chartData.length === 0 ? (
          <ChartEmptyState />
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid {...chartGridProps} />
              <XAxis dataKey="weekLabel" tick={chartAxisTick} />
              <YAxis allowDecimals={false} tick={chartAxisTick} />
              <Tooltip
                contentStyle={chartTooltipContentStyle}
                labelStyle={chartTooltipLabelStyle}
                itemStyle={chartTooltipItemStyle}
                formatter={(value, name) => {
                  if (name === 'count') return [value, 'Segnalazioni'];
                  if (name === 'alertsTriggered') return [value, 'Alert'];
                  return [value, name];
                }}
                labelFormatter={(label) => `Settimana del ${label}`}
              />
              <Bar dataKey="count" name="count" fill="var(--c-text-0)" radius={[4, 4, 0, 0]} />
              <Bar dataKey="alertsTriggered" name="alertsTriggered" fill="var(--c-warning)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className={styles.twoCol}>
        {/* Top zone */}
        <div className={styles.section}>
          <span className={styles.sectionTitle}>Zone più segnalate</span>
          {data.topZones.length === 0 ? (
            <ChartEmptyState />
          ) : (
            <CustomTable
              headers={['Zona', 'Segnalazioni', 'Intensità media']}
              data={data.topZones}
              fields={{
                Zona: {
                  key: 'zone',
                  customValue: (value: BodyMapAnalyticsZone['zone']) => ZONE_LABELS[value] ?? value,
                },
                Segnalazioni: { key: 'count', align: 'end' },
                'Intensità media': {
                  key: 'avgIntensity',
                  align: 'end',
                  customComponent: (_: any, z: BodyMapAnalyticsZone) => (
                    <span className={[styles.intensityBadge, avgIntensityClass(z.avgIntensity)].join(' ')}>
                      {z.avgIntensity.toFixed(1)}
                    </span>
                  ),
                },
              }}
            />
          )}
        </div>

        {/* Distribuzione per tipo */}
        <div className={styles.section}>
          <span className={styles.sectionTitle}>Distribuzione per tipo</span>
          {data.typeDistribution.length === 0 ? (
            <ChartEmptyState />
          ) : (
            <CustomTable
              headers={['Tipo', 'Segnalazioni']}
              data={data.typeDistribution}
              fields={{
                Tipo: { key: 'type', customValue: (value: string) => TYPE_LABELS[value] ?? value },
                Segnalazioni: { key: 'count', align: 'end' },
              }}
            />
          )}
        </div>
      </div>

      {/* Top atleti */}
      {data.topAthletes.length > 0 && (
        <div className={styles.section}>
          <span className={styles.sectionTitle}>Atleti con più segnalazioni</span>
          <CustomTable
            headers={['Atleta', 'Segnalazioni', 'Intensità media']}
            data={data.topAthletes}
            fields={{
              Atleta: {
                key: 'athleteName',
                customValue: (value: string | undefined, a) => value ?? a.athleteId,
              },
              Segnalazioni: { key: 'count', align: 'end' },
              'Intensità media': {
                key: 'avgIntensity',
                align: 'end',
                customComponent: (_: any, a) => (
                  <span className={[styles.intensityBadge, avgIntensityClass(a.avgIntensity)].join(' ')}>
                    {a.avgIntensity.toFixed(1)}
                  </span>
                ),
              },
            }}
          />
        </div>
      )}
    </div>
  );
};

export default TeamBodyMapAnalytics;
